/**
 * The per-entity Terraform section (Feature: terraform-provider, task 10.2; **Requirements 14.1, 14.2,
 * 14.5, 14.6, 14.7**; Design: §Components 10, 11, Algorithm L).
 *
 * Everything a reader needs to bring one dashboard-created monitor under Terraform management: the exact
 * `terraform import` command, the `resource` stanza that matches it, and a collapsed copy of the provider
 * configuration. The point of the whole surface is that adoption should need **no id hunting and no
 * hand-written HCL** — so the id in the command and the attributes in the stanza both come from the DTO
 * the page already loaded, and the suggested resource name is derived once and used in both.
 *
 * ## Why the name is normalised in one place and used in two
 *
 * A `logical_name` is free text; a Terraform resource name is an identifier. `toHclIdentifier` bridges
 * them (Requirement 14.4), and it is **idempotent**, which is what makes it safe for the renderer to
 * normalise again internally. The command and the stanza therefore cannot disagree about the address
 * being imported — a mismatch there is the one error that makes both snippets useless together while each
 * looks correct alone. An Alert_Rule has no `logical_name`, so its name comes from the rule id.
 *
 * ## Requirement 14.7 is a claim about this component
 *
 * It issues no request. Not a lazy one, not a cached one, not one behind a `useEffect`. Everything
 * rendered here is a pure function of props plus {@link useConfig}, and `TerraformSection.test.tsx`
 * asserts it from the outside with a client whose every method fails the test if called. That matters
 * because the alternative — fetching a "renderable" representation from the API — would put HCL rendering
 * on the server and make every dashboard page load pay for a section most readers never open.
 */

import type { AlertRuleDto, CheckDto } from '../api/types';
import { CopyableSnippet } from '../terraform/CopyableSnippet';
import {
  renderAlertRuleResource,
  renderCheckResource,
  renderImportCommand,
  toHclIdentifier,
  type SoroushResourceType,
} from '../terraform/hcl';
import { TerraformSetup } from './TerraformSetup';

/** The entity this section renders for. A union, because the two shapes share no identifier field. */
export type TerraformSubject =
  | { readonly kind: 'check'; readonly check: CheckDto }
  | { readonly kind: 'alert-rule'; readonly rule: AlertRuleDto };

/**
 * The anchor the Alert_Rules list links to (`/alert-rules/:id/edit#terraform`), so a row's "Terraform"
 * link lands on this section rather than at the top of the edit page (Requirement 14.2).
 */
export const TERRAFORM_SECTION_ID = 'terraform';

/** What each subject contributes: the resource type, the id to import, and the suggested name. */
function describe(subject: TerraformSubject): {
  resourceType: SoroushResourceType;
  entityId: string;
  suggestedName: string;
  origin: string | undefined;
  entityLabel: 'check' | 'alert rule';
  stanza: string;
} {
  if (subject.kind === 'check') {
    const { check } = subject;
    const suggestedName = toHclIdentifier(check.logicalName);
    return {
      resourceType: 'soroush_check',
      entityId: check.checkId,
      suggestedName,
      origin: check.origin,
      entityLabel: 'check',
      stanza: renderCheckResource(check, suggestedName),
    };
  }
  const { rule } = subject;
  // An Alert_Rule has no logical name (Requirement 14.4 says "or the rule id"), so the id is the name.
  const suggestedName = toHclIdentifier(rule.ruleId);
  return {
    resourceType: 'soroush_alert_rule',
    entityId: rule.ruleId,
    suggestedName,
    origin: rule.origin,
    entityLabel: 'alert rule',
    stanza: renderAlertRuleResource(rule, suggestedName),
  };
}

export function TerraformSection({ subject }: { subject: TerraformSubject }): JSX.Element {
  const { resourceType, entityId, suggestedName, origin, entityLabel, stanza } = describe(subject);
  const importCommand = renderImportCommand(resourceType, suggestedName, entityId);

  return (
    <section className="card terraform-section" id={TERRAFORM_SECTION_ID} data-testid="terraform-section">
      <h2>Terraform</h2>
      <p className="muted">
        Run the import, then add the stanza to your configuration. The import brings this {entityLabel}{' '}
        under Terraform management without re-creating it, so its id, history, and state survive the move.
      </p>

      {/*
       * Requirement 14.5 — the manifest consequence, said out loud on the page as well as in a comment
       * inside the stanza. Both, deliberately: the page note is what a reader deciding whether to adopt
       * the entity sees, and the stanza comment is what survives being pasted into a repository where
       * nobody remembers this page. Security finding 6 is that this transfer is otherwise silent.
       */}
      {origin === 'manifest' ? (
        <p role="note" className="adoption-notice" data-testid="terraform-manifest-note">
          This {entityLabel} is managed by the deployment manifest. Importing it and applying moves it to
          Terraform for good — it leaves manifest reconciliation permanently, and the next{' '}
          <code>PUT /v1/deployments</code> reports it as a conflict rather than a change.
        </p>
      ) : null}

      <CopyableSnippet label="Import command" text={importCommand} testId="terraform-import-command" />
      <CopyableSnippet label="Resource stanza" text={stanza} testId="terraform-resource-stanza" />

      {/* Requirement 14.6, second half: reachable here so a non-admin who was handed a key can see it. */}
      <TerraformSetup collapsed />
    </section>
  );
}
